import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  AfterViewInit,
  Component,
  Inject,
  Input,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { catchError, finalize, lastValueFrom, map, Observable, of, throwError } from 'rxjs';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import {
  Domande,
  InformativaAziendale,
  InformativaAziendaleService,
  Sezioni,
} from 'src/app/services/informativa-aziendale/informativa-aziendale.service';
import {
  StandardRendicontazioneService,
  STDR,
} from 'src/app/services/standard-rendicontazione/standard-rendicontazione.service';
import {
  CategorieUm,
  UnitaDiMisura,
  UnitaDiMisuraService,
} from 'src/app/services/unita-di-misura/unita-di-misura.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogCreaDomandeInformativaComponent } from '../dialog-crea-domande-informativa/dialog-crea-domande-informativa.component';
import { DialogDdDomandeComponent } from '../dialog-dd-domande/dialog-dd-domande.component';

@Component({
  selector: 'app-dialog-crea-informativa-aziendale',
  templateUrl: './dialog-crea-informativa-aziendale.component.html',
  styleUrls: ['./dialog-crea-informativa-aziendale.component.scss'],
  standalone: false,
})
export class DialogCreaInformativaAziendaleComponent implements AfterViewInit {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  @ViewChildren('inputDesc', { read: CdkTextareaAutosize })
  queryListTextarea!: QueryList<CdkTextareaAutosize>;
  @ViewChildren('inputLineaGuida', { read: CdkTextareaAutosize })
  queryListTextareaLineaGuida!: QueryList<CdkTextareaAutosize>;

  @ViewChildren(DialogCreaDomandeInformativaComponent)
  cmpDomande!: QueryList<DialogCreaDomandeInformativaComponent>;

  private _sezioniArraySelDomande: FormArray<FormGroup<any>> = new FormArray<FormGroup<any>>([]);

  @Input()
  set sezioniArraySelDomande(sezioniArraySelDomande: FormArray<FormGroup<any>>) {
    this._sezioniArraySelDomande = sezioniArraySelDomande;
  }

  get sezioniArraySelDomande(): FormArray<FormGroup<any>> {
    return this._sezioniArraySelDomande;
  }

  public arrayCategorie: CategorieUm[] = [];
  public mappaCategorieUm: { [idCategoriaUnitaDiMisura: string]: UnitaDiMisura[] } = {};
  public arrayUm: UnitaDiMisura[] = [];

  public stdrArray: STDR[] = [];
  public complianceArray: STDR[] = [];

  /* Form Controls */
  public formInfoAz = new FormGroup({
    /* Id */
    idInformativa: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    chiave: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    codiceLingua: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    id: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    stato: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    versione: new FormControl<number | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    /* titolo */
    titolo: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
    stdr: new FormControl<STDR | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    compliance: new FormControl<STDR | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    /* descrizione */
    descrizione: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
    sezioni: new FormArray([]),
  });

  constructor(
    public dialogRef: MatDialogRef<DialogCreaInformativaAziendaleComponent>,
    private readonly informativaAziendale: InformativaAziendaleService,
    private readonly utilityService: UtilityService,
    private readonly unitaDiMisura: UnitaDiMisuraService,
    private readonly stdrService: StandardRendicontazioneService,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      informativaAziendale: InformativaAziendale;
    },
  ) {}

  ngAfterViewInit(): void {
    this.spinnerOver.show();

    setTimeout(async () => {
      this.unitaDiMisura.getCategoria().subscribe((categorie) => {
        this.arrayCategorie = categorie;
        this.arrayCategorie.forEach((cate) => {
          if (cate?.id) {
            this.mappaCategorieUm[cate.id] = cate.unitaDiMisura || [];
          }
        });
      });

      await this.getStdr();
      this._initInfoAz(this.data.informativaAziendale);
    }, 0);
    this.spinnerOver.hide();
  }

  /**
   * Metodo che mi pulisce la form
   */
  private _resetForm(informativa: InformativaAziendale) {
    // Reset della form, tranne dei campi stdr e compliance
    this.formInfoAz.reset({
      stdr: this.formInfoAz.get('stdr')?.value,
      compliance: this.formInfoAz.get('compliance')?.value,
      titolo: informativa.titolo ?? this.formInfoAz.get('titolo')?.value,
      descrizione: informativa.descrizione ?? this.formInfoAz.get('descrizione')?.value,
    });

    //Pulisco il form array delle sezioni
    (this.formInfoAz.get('sezioni') as FormArray).clear();
  }

  compareSTDR(stdr1: STDR, stdr2: STDR): boolean {
    return stdr1?.id === stdr2?.id; // Sostituisci "id" con la proprietà univoca dei stdr
  }

  private _initInfoAz(informativa: InformativaAziendale): void {
    if (informativa?.stdr?.compliance) {
      informativa.compliance = informativa.stdr;
      delete informativa.stdr;
    }

    if (informativa?.sezioni?.length) {
      Object.keys(informativa).forEach((key) => {
        if (key !== 'sezioni' && this.formInfoAz.get(key)) {
          this.formInfoAz.get(key)?.setValue((informativa as any)[key]);
        }
      });

      this._initSezioni(informativa.sezioni);
    } else {
      this.addSezione();
    }

    const stdrControl = this.formInfoAz.get('stdr');
    const complianceControl = this.formInfoAz.get('compliance');

    if (stdrControl?.getRawValue()) {
      complianceControl?.disable();
    } else if (complianceControl?.getRawValue()) {
      stdrControl?.disable();
    }

    this.formInfoAz.updateValueAndValidity();
  }

  onSelectCompliance(): void {
    const compliance = this.formInfoAz.get('compliance');
    const stdr = this.formInfoAz.get('stdr');

    const selectedCompliance = compliance?.getRawValue() as STDR | null;

    if (selectedCompliance) {
      stdr?.disable();
    } else {
      stdr?.enable();
    }

    stdr?.updateValueAndValidity();
    this._getBozza(selectedCompliance?.codice);
  }

  onSelectSTDR(): void {
    const compliance = this.formInfoAz.get('compliance');
    const stdr = this.formInfoAz.get('stdr');

    const selectedSTDR = stdr?.getRawValue() as STDR | null;

    if (selectedSTDR) {
      compliance?.disable();
    } else {
      compliance?.enable();
    }

    compliance?.updateValueAndValidity();
    this._getBozza(selectedSTDR?.codice);
  }

  /**
   * Mappatura form informativa aziendale
   * @param sezioni
   */
  private _initSezioni(sezioni: Sezioni[]) {
    (this.formInfoAz.get('sezioni') as FormArray).clear();

    sezioni.forEach((value, index, array) => {
      const formSezioni = new FormGroup({
        chiave: new FormControl<string | undefined>(value.chiave || undefined),
        nomeBreve: new FormControl<string | undefined>(value.nomeBreve || undefined),
        id: new FormControl<string | undefined>(value.id || undefined),
        descrizione: new FormControl<string | undefined>(value.descrizione || undefined),
        titolo: new FormControl<string | undefined>(value.titolo || undefined),
        domande: new FormArray([]),
      });

      value.domande.forEach((domanda) => {
        const formDomanda = new FormGroup({
          chiave: new FormControl<string | undefined>(domanda.chiave || undefined),
          domanda: new FormControl<string | undefined>(domanda.domanda || undefined, [
            Validators.required,
            UtilityService.blankValidator,
          ]),

          metadati: new FormGroup({
            lineaGuida: new FormArray(
              // Se domanda.metadati.lineaGuida è definito e ha elementi, mappiamo il suo contenuto,
              // altrimenti inizializziamo con un array contenente un elemento vuoto.
              (domanda?.metadati?.lineaGuida?.length ? domanda.metadati.lineaGuida : ['']).map(
                (linea) => new FormControl(linea),
              ),
            ),
          }),

          id: new FormControl<string | undefined>(domanda.id || undefined),
          note: new FormControl<string | undefined>(domanda.note || undefined),
          obbligatorio: new FormControl<boolean>(domanda.obbligatorio || false),
          sottodomanda: new FormControl<boolean>(domanda.sottodomanda || false),
          tipo: new FormControl<string | undefined>(domanda.tipo || undefined, [
            Validators.required,
            UtilityService.blankValidator,
          ]),
          idCategoriaUnitaDiMisura: new FormControl<string | undefined>(
            domanda.idCategoriaUnitaDiMisura || undefined,
          ),
          idUnitaDiRiferimento: new FormControl<string | undefined>(
            domanda.idUnitaDiRiferimento || undefined,
          ),
          opzioni: new FormArray([]),
          moduloCompleto: new FormControl<boolean>(domanda.moduloCompleto || false),
        });

        (domanda.opzioni || []).forEach((value, index, array) => {
          const opzioni = new FormGroup({
            chiave: new FormControl<string | undefined>(value.chiave || undefined),
            id: new FormControl<string | undefined>(value.id || undefined),
            valore: new FormControl<string | undefined>(value.valore || undefined),
            moduloCompleto: new FormControl<boolean>(value.moduloCompleto || false),
          });

          (formDomanda.get('opzioni') as FormArray).push(opzioni);
        });

        (formSezioni.get('domande') as FormArray).push(formDomanda);
      });

      (this.formInfoAz.get('sezioni') as FormArray).push(formSezioni);
    });

    setTimeout(() => {
      this.queryListTextarea?.forEach((area) => area.resizeToFitContent(true));
      this.queryListTextareaLineaGuida?.forEach((area) => area.resizeToFitContent(true));
    }, 0);
  }

  // Metodo per aggiungere una nuova linea guida
  addLineaGuida(domandaArr: any) {
    (domandaArr.controls.metadati.controls.lineaGuida as FormArray).push(new FormControl(''));
  }

  /**
   * Construisce una sezione di defualt in FORM
   */
  addSezione() {
    const formSezioni = new FormGroup({
      chiave: new FormControl<string | undefined>(undefined),
      nomeBreve: new FormControl<string | undefined>(undefined),
      id: new FormControl<string | undefined>(undefined),
      descrizione: new FormControl<string | undefined>(undefined),
      titolo: new FormControl<string | undefined>(undefined),
      domande: new FormArray([]),
    });
    (this.formInfoAz.get('sezioni') as FormArray).push(formSezioni);
  }

  /**
   * Salvataggio informativa aziendale
   */
  salvaInfoAz() {
    this.spinnerOver.show();

    this.salva().subscribe({
      next: (risp) => {
        if (risp) {
          this.utilityService.openSnackBar('Salvataggio effettuato ', '', {
            duration: 2000,
            panelClass: ['success-snackbar'],
          });

          this.dialogRef.close();
        } else {
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: 'Errore nel salvataggio',
            bottoni: [{ nome_btn: 'Chiudi' }],
          });
        }
        this.spinnerOver.hide();
      },
      error: (err) => {
        console.error(err);

        let msgErr = 'Errore nel salvataggio';

        if (err === 'NO_OPZ') {
          msgErr = 'Non è possibile salvare questa informativa aziendale, non ci sono risposte';
        } else if (err === 'NO_DOM') {
          msgErr = 'Non è possibile salvare questa informativa aziendale, non ci sono domande';
        } else if (err === 'NO_UM') {
          msgErr =
            'Non è possibile salvare questa informativa aziendale, non ci sono unità di misura';
        } else if (err === 'NO_SEZ') {
          msgErr = 'Non è possibile salvare questa informativa aziendale, non ci sono domande';
        }

        this.utilityService.openDialog({
          titolo: 'Attenzione',
          descrizione: msgErr,
          bottoni: [{ nome_btn: 'Chiudi' }],
        });
        this.spinnerOver.hide();
      },
    });
  }

  salva(): Observable<boolean> {
    const form = this.mapValoriDomanda();

    if (!form.sezioni.length || !(form.sezioni[0] as any)?.domande?.length) {
      return throwError(() => 'NO_SEZ');
    } else {
      const sezVuote = form.sezioni
        .map((val: any, index, array) => {
          return val.domande
            .map((dom: Domande) => {
              if (!dom.domanda || !dom.tipo) {
                return 'NO_DOM';
              } else if (
                (dom.tipo === 'scelta_multipla' || dom.tipo === 'scelta_singola') &&
                !dom.opzioni?.length
              ) {
                return 'NO_OPZ';
              } else if (
                (dom.tipo === 'numero_intero' || dom.tipo === 'numero_decimale') &&
                (!dom.idCategoriaUnitaDiMisura || !dom.idUnitaDiRiferimento)
              ) {
                return 'NO_UM';
              } else {
                return null;
              }
            })
            .find((val: any) => !!val);
        })
        .find((val: any) => !!val);

      if (sezVuote) {
        return throwError(() => sezVuote);
      }
    }

    if (this.formInfoAz.valid) {
      if (form.compliance) {
        form.stdr = form.compliance;
        form.compliance = undefined;
      }

      const idInformativa = this.formInfoAz.get('idInformativa')?.value;
      if (idInformativa) {
        return this.informativaAziendale.put(idInformativa, form).pipe(
          map((risp) => {
            return true;
          }),
          catchError((err) => {
            console.error('Errore durante il salvataggio :', err);
            return of(false);
          }),
        );
      } else {
        return this.informativaAziendale.post(form).pipe(
          map((risp) => {
            if (risp.body) {
              this.formInfoAz.get('idInformativa')?.setValue(risp.body.idInformativa);
            }
            return true;
          }),
          catchError((err) => {
            console.error('Errore durante il salvataggio :', err);
            return of(false);
          }),
        );
      }
    } else {
      Object.values(this.formInfoAz.controls).forEach((control) => {
        control.markAsTouched();
      });
      return of(true);
    }
  }

  /**
   * Aggiunge una domanda vuota tramite il bottone
   * @param formArrayDomande
   */
  addDomanda(formArrayDomande: FormArray) {
    const formDomanda = new FormGroup({
      chiave: new FormControl<string | undefined>(undefined),
      domanda: new FormControl<string | undefined>(undefined, [
        Validators.required,
        UtilityService.blankValidator,
      ]),

      metadati: new FormGroup({
        lineaGuida: new FormArray(
          // Inizializziamo con un FormControl vuoto
          [new FormControl('')],
        ),
      }),

      /* Aggiungi id temporaneo per sincronizzare le opzioni dal figlio al padre, poi lo rimuovo in salvataggio */
      id: new FormControl<string | undefined>(this.generateId('TEMPORANEO')),
      note: new FormControl<string | undefined>(undefined),
      obbligatorio: new FormControl<boolean>(false),
      sottodomanda: new FormControl<boolean>(false),
      idCategoriaUnitaDiMisura: new FormControl<string | undefined>(undefined),
      idUnitaDiRiferimento: new FormControl<string | undefined>(undefined),
      tipo: new FormControl<string | undefined>(undefined, [
        Validators.required,
        UtilityService.blankValidator,
      ]),
      opzioni: new FormArray([]),
      moduloCompleto: new FormControl<boolean>(false),
    });

    formArrayDomande.push(formDomanda);
  }

  public generateId(prefix: string): string {
    return prefix + Date.now().toString();
  }

  elimina(formArray: FormArray, index: number, idDomanda?: string) {
    formArray.removeAt(index);
  }

  drop(event: CdkDragDrop<any[]>, formSezione: FormGroup) {
    const formArrayDomande = formSezione.get('domande') as FormArray;
    const currentGroup = formArrayDomande.at(event.previousIndex);
    formArrayDomande.removeAt(event.previousIndex, { emitEvent: false });
    formArrayDomande.insert(event.currentIndex, currentGroup, { emitEvent: false });
  }

  cambiaTipologia(event: any, domandeArr: FormGroup) {
    if (event.value !== 'numero_intero' && event.value !== 'numero_decimale') {
      domandeArr.get('idUnitaDiRiferimento')?.setValue(undefined);
      domandeArr.get('idCategoriaUnitaDiMisura')?.setValue(undefined);
    }
  }

  pubblica() {
    this.utilityService.openDialog({
      descrizione: "Rendere pubblica l'Informativa Aziendale?",
      bottoni: [
        {
          nome_btn: 'NO',
          background_color: 'var(--mat-color-accent)',
        },
        {
          nome_btn: 'SI',
          background_color: 'var(--mat-color-primary)',
          handler: () => {
            this.spinnerOver.show();
            this.salva().subscribe({
              next: (risposta) => {
                if (risposta) {
                  const idInformativa = this.formInfoAz.get('idInformativa')?.value;
                  if (idInformativa) {
                    this.informativaAziendale.putPubblica(idInformativa).subscribe({
                      next: (risp) => {
                        this.utilityService.openSnackBar(
                          'Configurazione Informativa Aziendale PUBBLICATA',
                          '',
                          {
                            duration: 2000,
                            panelClass: ['success-snackbar'],
                          },
                        );
                        this.dialogRef.close();
                      },
                      error: (err) => {
                        this.utilityService.openDialog({
                          titolo: 'Attenzione',
                          descrizione: 'Errore nella pubblicazione ',
                          bottoni: [{ nome_btn: 'Chiudi' }],
                        });
                        this.spinnerOver.hide();
                      },
                    });
                  }
                } else {
                  this.utilityService.openDialog({
                    titolo: 'Attenzione',
                    descrizione: 'Errore nella pubblicazione ',
                    bottoni: [{ nome_btn: 'Chiudi' }],
                  });
                }
                this.spinnerOver.hide();
              },
              error: (err) => {
                console.error(err);
                let msgErr = 'Errore nel salvataggio';

                if (err === 'NO_OPZ') {
                  msgErr =
                    'Non è possibile salvare questa informativa aziendale, non ci sono risposte';
                } else if (err === 'NO_DOM') {
                  msgErr =
                    'Non è possibile salvare questa informativa aziendale, non ci sono domande';
                } else if (err === 'NO_UM') {
                  msgErr =
                    'Non è possibile salvare questa informativa aziendale, non ci sono unità di misura';
                } else if (err === 'NO_SEZ') {
                  msgErr =
                    'Non è possibile salvare questa informativa aziendale, non ci sono domande';
                }

                this.utilityService.openDialog({
                  titolo: 'Attenzione',
                  descrizione: msgErr,
                  bottoni: [{ nome_btn: 'Chiudi' }],
                });
                this.spinnerOver.hide();
              },
            });
          },
        },
      ],
    });
  }

  scrollaInView(elemento: HTMLElement) {
    setTimeout(() => {
      elemento.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',

        inline: 'start',
      });
    }, 300);
  }

  /**
   *  Get stdr
   * @param settoriIds
   */
  async getStdr() {
    const idInformativa = this.data?.informativaAziendale?.idInformativa;
    const isCreazione = !idInformativa;

    this.spinnerOver.show();
    try {
      const esito: STDR[] = await lastValueFrom(
        this.informativaAziendale.getStdr(idInformativa, isCreazione),
      );
      this.stdrArray = esito.filter((stdr) => !stdr.compliance);
      this.complianceArray = esito.filter((compl) => compl.compliance);
    } catch (err) {
      console.error(err);
    } finally {
      this.spinnerOver.hide(); // Assicura che lo spinner venga sempre nascosto
    }
  }

  /**
   * Metodo che mi intercetta tramite il viewchildren le form con o senza modifiche, e se le sostituisco alla form Padre.
   * Per riuscire a interccetare le modifiche fatte nel componente figlio.
   * Questo perchè controllo il form Domanda sul filgio e devo mantenere il padre allineato nel salvataggio.
   * @returns
   */
  mapValoriDomanda() {
    const formValue = this.formInfoAz.getRawValue();

    formValue.sezioni.forEach((sez: any) => {
      this.cmpDomande.forEach((componente, index) => {
        const idDomanda = componente.formDomanda.controls.id.value;

        const domandaIndex = sez.domande.findIndex((dom: any) => dom.id === idDomanda);

        if (domandaIndex !== -1) {
          sez.domande[domandaIndex] = componente.formDomanda.getRawValue();
        }
      });
    });

    this.azzeraIdTemporanei(formValue);

    return formValue;
  }

  /**
   * Azzero gli id temporanei
   * @param formValue form da mandare in salvataggio
   */
  azzeraIdTemporanei(formValue: any) {
    formValue?.sezioni?.forEach((sezione: any) => {
      sezione?.domande?.forEach((domanda: any) => {
        if (typeof domanda.id === 'string' && domanda.id.includes('TEMPORANEO')) {
          domanda.id = '';
        }
      });
    });
  }

  /* Configurazione selezione DOMANDE */
  public arraySelSuCfgRisposte: any[] = [];

  apriCfgDomande() {
    const form = this.mapValoriDomanda();

    this._initSezioni(form.sezioni);

    const dialogCfgRisposteTematica = this.dialog.open(DialogDdDomandeComponent, {
      data: {
        sezioni: this.formInfoAz.get('sezioni') as FormArray,
      },
      panelClass: ['dialog-container'],
      disableClose: true,
      width: '99%',
      height: '100%',
      autoFocus: false,
    });

    dialogCfgRisposteTematica.afterClosed().subscribe((result) => {
      if (result) {
        this._initSezioni(result.sezioni);
      }
    });
  }

  /**
   * Metodo che mi valorizza l'array degli elementi selezionati in configurazione della risposta
   * @param formArrayRisposte oggetto con gli elementi
   * @param tipoElemento tipo elemento, se kpòi o azione
   */
  getArrayEelemSelezionati(formArrayRisposte: FormArray) {
    this.arraySelSuCfgRisposte = [];

    formArrayRisposte.value.forEach((elem: any) => {
      this.arraySelSuCfgRisposte.push({
        ...elem.valore,
        valoreAttribuito: elem.punteggio,
      });
    });
  }

  /**
   * Get bozza di un informativa già creata se esiste
   * @param codiceStdr
   */
  private _getBozza(codiceStdr: string | undefined) {
    this.spinnerOver.show();

    this.informativaAziendale
      .getBozza(codiceStdr)
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe({
        next: (informativa: InformativaAziendale) => {
          // Pulisco la form
          this._resetForm(informativa);
          this._initInfoAz(informativa);
        },
        error: (err) => {
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: 'Errore bozza ',
            bottoni: [{ nome_btn: 'Chiudi' }],
          });
        },
      });
  }

  /**
   * Controllo se le categorie sono state valorizzate, cosi da fare generale il componente filgio (le domande)
   */
  ctrArrayCategorie(): boolean {
    return !!(this.arrayCategorie.length && Object.keys(this.mappaCategorieUm).length > 0);
  }

  /**
   * Metodo che mi sincornizza la domanda inserita nel componente figlio, per poi visualizzarla sul titolo del panel
   * @param domandaInserita domanda che mi arriva dal componente figlio
   * @param domandeArr form group dove è presente la domanda
   */
  sincronizzaDomanda(domandaInserita: string, domandeArr: any) {
    domandeArr.get('domanda').setValue(domandaInserita);
  }
}
